import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import { Images, Upload, Loader, Trash2, Pencil, Check, X, MessageSquare, AlertCircle } from 'lucide-react';
import { buildTeamApiService } from '../../services/buildTeamApi';
import type { BuildProjectMedia } from '../../types/buildTeam';
import { BuildProjectMediaThumbnail } from './BuildProjectMediaThumbnail';
import { ConfirmationModal } from '../ConfirmationModal';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';

export interface BuildProjectGallerySectionProps {
  projectId: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

type GalleryFilter = 'all' | 'direct' | 'comments';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function BuildProjectGallerySection({
  projectId,
  canCreate,
  canEdit,
  canDelete,
  onUpdate,
}: BuildProjectGallerySectionProps) {
  const [images, setImages] = useState<BuildProjectMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState<GalleryFilter>('all');
  const [lightbox, setLightbox] = useState<BuildProjectMedia | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await buildTeamApiService.listProjectImages(projectId, 1, 200);
      setImages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const filtered = useMemo(() => {
    if (filter === 'direct') return images.filter((m) => !m.source_comment_id);
    if (filter === 'comments') return images.filter((m) => !!m.source_comment_id);
    return images;
  }, [images, filter]);

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    setDeletingId(confirmDeleteId);
    try {
      await buildTeamApiService.deleteProjectMedia(confirmDeleteId);
      setConfirmDeleteId(null);
      await loadImages();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete image');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-32" />
          <SkeletonLoader className="h-8 w-40" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <SkeletonLoader className="aspect-video w-full rounded-lg" />
              <SkeletonLoader className="h-3 w-3/4" />
              <SkeletonLoader className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load gallery"
        description={error}
        action={
          <button type="button" onClick={loadImages} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2 min-w-0">
          <Images className="w-4 h-4 text-accent-300 shrink-0" />
          <span className="truncate">Pictures</span>
          <span className="badge badge-neutral">{images.length}</span>
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="segmented" role="group" aria-label="Gallery filter">
            {(
              [
                ['all', 'All'],
                ['direct', 'Direct'],
                ['comments', 'Comments'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className="segmented-item segmented-item-sm"
                data-active={filter === key ? 'true' : 'false'}
              >
                {label}
              </button>
            ))}
          </div>
          {canCreate && (
            <button
              type="button"
              data-tour="build-gallery-upload"
              onClick={() => setShowUpload(!showUpload)}
              className="glass-button-sm"
              aria-expanded={showUpload}
            >
              <Upload className="w-4 h-4" />
              Add pictures
            </button>
          )}
        </div>
      </div>

      {showUpload && canCreate && (
        <GalleryUploadForm
          projectId={projectId}
          onDone={async () => {
            setShowUpload(false);
            await loadImages();
            onUpdate();
          }}
          onCancel={() => setShowUpload(false)}
        />
      )}

      {filtered.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={Images}
          title={images.length === 0 ? 'No pictures yet' : 'No pictures in this filter'}
          description={
            images.length === 0
              ? canCreate
                ? 'Add progress shots and references here, or attach images to a comment and they appear here too.'
                : 'Pictures added to this project, and images attached to comments, appear here.'
              : 'Try a different filter to see the other pictures.'
          }
          action={images.length === 0 && canCreate && !showUpload ? <button type="button" className="btn-primary" onClick={() => setShowUpload(true)}><Upload className="w-4 h-4" />Add pictures</button> : undefined}
        />
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((m) => (
            <li key={m.id} className="flex flex-col gap-2">
              <BuildProjectMediaThumbnail
                media={m}
                className="aspect-video w-full"
                onClick={() => setLightbox(m)}
              />
              <div className="space-y-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] text-glass-500 truncate font-mono">{m.filename}</span>
                  {m.source_comment_id && (
                    <span
                      className="chip shrink-0"
                      title="From a comment"
                    >
                      <MessageSquare className="w-2.5 h-2.5" />
                      Comment
                    </span>
                  )}
                </div>
                <GalleryCaptionRow
                  media={m}
                  canEdit={canEdit}
                  onSaved={loadImages}
                />
                <div className="flex items-center justify-between text-[10px] text-glass-500 tabular-nums">
                  <span>{formatFileSize(m.size)}</span>
                  <time dateTime={new Date(m.created_at).toISOString()}>
                    {format(new Date(m.created_at), 'MMM d, yyyy')}
                  </time>
                </div>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(m.id)}
                    className="glass-button-sm w-full text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {lightbox && (
        <button
          type="button"
          className="modal-overlay z-[10000]"
          onClick={() => setLightbox(null)}
          aria-label="Close image preview"
        >
          <div
            className="max-w-[min(96vw,1200px)] max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <BuildProjectMediaThumbnail
              media={lightbox}
              className="max-h-[85vh] w-auto max-w-full border-glass-200/30"
              imgClassName="object-contain max-h-[85vh]"
            />
            {lightbox.caption && (
              <p className="mt-2 text-sm text-glass-200 text-center">{lightbox.caption}</p>
            )}
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-2 right-2 btn-icon rounded-full bg-dark-900/70 border-glass-200/20 text-white hover:bg-dark-900/90"
              aria-label="Close"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </button>
      )}

      {confirmDeleteId && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={handleDelete}
          title="Delete image"
          message="Remove this image from the project gallery? This cannot be undone."
          confirmText="Delete"
          isLoading={!!deletingId}
        />
      )}
    </div>
  );
}

function GalleryCaptionRow({
  media,
  canEdit,
  onSaved,
}: {
  media: BuildProjectMedia;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(media.caption || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(media.caption || '');
  }, [media.caption, editing]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await buildTeamApiService.updateProjectMediaCaption(media.id, {
        caption: draft.trim() ? draft.trim() : null,
      });
      setEditing(false);
      await onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update caption');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-1">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Caption"
          className="glass-input w-full text-xs"
          disabled={saving}
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="glass-button-sm"
          >
            {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setDraft(media.caption || '');
            }}
            disabled={saving}
            className="glass-button-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1 min-h-[1.25rem]">
      <p className="text-xs text-glass-400 flex-1 line-clamp-2">
        {media.caption || <span className="italic text-glass-500">No caption</span>}
      </p>
      {canEdit && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-glass-500 hover:text-white p-0.5 rounded shrink-0"
          title="Edit caption"
          aria-label="Edit caption"
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function GalleryUploadForm({
  projectId,
  onDone,
  onCancel,
}: {
  projectId: string;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: { 'image/*': [] },
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (acceptedFiles.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (caption.trim()) formData.append('caption', caption.trim());
      acceptedFiles.forEach((file, i) => {
        formData.append(`files[${i}]`, file);
      });
      await buildTeamApiService.uploadProjectImages(projectId, formData);
      await onDone();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="subcard p-4 space-y-3"
    >
      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
          isDragActive ? 'border-accent-500/50 bg-accent-500/5' : 'border-glass-200/20 hover:border-glass-200/40'
        )}
      >
        <input {...getInputProps()} />
        <span className="icon-tile icon-tile-neutral mx-auto mb-2">
          <Upload className="w-4 h-4" />
        </span>
        <p className="text-sm text-glass-300">
          {acceptedFiles.length > 0
            ? `${acceptedFiles.length} file(s) selected`
            : isDragActive
              ? 'Drop images here'
              : 'Choose images or drag them here'}
        </p>
      </div>
      <div>
        <label className="text-xs font-medium text-glass-400 mb-1.5 block">Caption (optional, applies to all)</label>
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className="glass-input w-full"
          placeholder="Shown on each uploaded image"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || acceptedFiles.length === 0}
          className="btn-primary"
        >
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload
        </button>
      </div>
    </form>
  );
}
